/* ---------------------------------------------------------------------------
   motion.ts — VERBATIM port from ~/FrontendFinal/Design/src/styles/motion.ts.

   Copied unchanged into the backend so the ported design components animate
   exactly as they do in the Design app. No values differ; see the Design repo's
   header for provenance. Only the file's home has changed (styles/ -> upload
   design/), so no import in it needed repointing — it imports nothing.
   --------------------------------------------------------------------------- */

/** Durations in seconds (Framer Motion's unit). Milliseconds in comments. */
export const duration = {
  /** 150ms — instant feedback: press, checkbox, tiny fills. */
  instant: 0.15,
  /** 200ms — the default micro-interaction: hover, focus ring, colour shift. */
  micro: 0.2,
  /** 250ms — the slow end of micro: small reveals, tooltips, row highlight. */
  microSlow: 0.25,
  /** 300ms — fast section transition. */
  section: 0.3,
  /** 380ms — the default page/route transition. Mid-range of the 300-450 band. */
  page: 0.38,
  /** 450ms — the slowest permitted move: modal entrance, large panel. */
  pageSlow: 0.45,
} as const;

export const easing = {
  /** Entrances. Decisive departure, long settle. The dominant curve. */
  out: [0.16, 1, 0.3, 1],
  /** A gentler ease-out for large or heavy elements (modals, panels). */
  outSoft: [0.25, 1, 0.5, 1],
  /** State changes: hover, active, toggles. Symmetrical, composed. */
  inOut: [0.4, 0, 0.2, 1],
  /** Exits. Accelerates away; the mirror of `out`. */
  in: [0.7, 0, 0.84, 0],
  /** Linear, for opacity-only cross-fades where curvature reads as a stutter. */
  linear: [0, 0, 1, 1],
} as const;

/** CSS-string forms, for stylesheet transitions and non-Framer contexts. */
export const easingCss = {
  out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  outSoft: 'cubic-bezier(0.25, 1, 0.5, 1)',
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  in: 'cubic-bezier(0.7, 0, 0.84, 0)',
  linear: 'linear',
} as const;

export const travel = {
  /** 8px — the smallest legible move. Row highlights, inline reveals. */
  xs: 8,
  /** 12px — list items, table rows, cards in a grid. */
  sm: 12,
  /** 16px — section blocks, form field groups. */
  md: 16,
  /** 24px — the maximum. Modal entrance, page-level content. */
  lg: 24,
} as const;

/** Press feedback. Scale only; no shadow, no rotation, no translate. */
export const press = {
  /** Barely perceptible, deliberately. Engineered, not springy. */
  scale: 0.98,
} as const;

export const stagger = {
  /** 40ms — long lists (table rows), where 60ms would feel sluggish. */
  tight: 0.04,
  /** 50ms — the default. */
  base: 0.05,
  /** 60ms — short groups of 2-4, where the rhythm should be legible. */
  loose: 0.06,
} as const;

export const transition = {
  micro: { duration: duration.micro, ease: easing.inOut },
  microOut: { duration: duration.micro, ease: easing.out },
  press: { duration: duration.instant, ease: easing.inOut },
  entrance: { duration: duration.section, ease: easing.out },
  page: { duration: duration.page, ease: easing.out },
  modal: { duration: duration.pageSlow, ease: easing.outSoft },
  exit: { duration: duration.section, ease: easing.in },
} as const;

/** Rise and settle. The default entrance for any block of content. */
export const riseIn = {
  hidden: { opacity: 0, y: travel.md },
  visible: { opacity: 1, y: 0, transition: transition.entrance },
  exit: { opacity: 0, y: travel.xs, transition: transition.exit },
} as const;

/** A smaller rise, for items inside a staggered parent. */
export const riseInItem = {
  hidden: { opacity: 0, y: travel.sm },
  visible: { opacity: 1, y: 0, transition: transition.entrance },
} as const;

/** Opacity-only. For anything where movement would fight the layout. */
export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.section, ease: easing.linear } },
  exit: { opacity: 0, transition: { duration: duration.micro, ease: easing.linear } },
} as const;

export const pageTransition = {
  hidden: { opacity: 0, y: travel.sm },
  visible: { opacity: 1, y: 0, transition: transition.page },
  exit: { opacity: 0, y: -travel.xs, transition: transition.exit },
} as const;

/** Modal: the largest permitted travel, on the softest curve. */
export const modalIn = {
  hidden: { opacity: 0, y: travel.lg, scale: 0.995 },
  visible: { opacity: 1, y: 0, scale: 1, transition: transition.modal },
  exit: { opacity: 0, y: travel.sm, scale: 0.995, transition: transition.exit },
} as const;

/**
 * Direction of a wizard step change. +1 = forward (Next), -1 = back (Back), and
 * 0 = neutral (first open / deep-link, no prior step to move relative to).
 */
export type StepDirection = 1 | -1 | 0;

export const stepSlideIn = {
  hidden: (dir: StepDirection) => ({ opacity: 0, x: dir * travel.sm }),
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: duration.micro, ease: easing.out },
  },
  exit: (dir: StepDirection) => ({
    opacity: 0,
    x: dir * -travel.sm,
    transition: { duration: duration.instant, ease: easing.out },
  }),
} as const;

/**
 * Step-to-step transition for the upload wizard box: a PURE opacity cross-fade,
 * NO travel. See the Design repo's note for why `y` is deliberately absent here
 * (the frame does not move between steps; any travel reads as a jump).
 */
export const stepFadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.microSlow, ease: easing.inOut } },
  exit: { opacity: 0, transition: { duration: duration.microSlow, ease: easing.inOut } },
} as const;

/** Scrim: opacity only. Never moves. */
export const scrimIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.section, ease: easing.linear } },
  exit: { opacity: 0, transition: { duration: duration.micro, ease: easing.linear } },
} as const;

export function staggerContainer(interval: number = stagger.base, delayChildren = 0.05) {
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: interval, delayChildren },
    },
  } as const;
}

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
